/**
 * **The one place the counting rule lives.**
 *
 * A workspace-level domain rule, recorded in the root `CLAUDE.md` under "Warm-up is the only set
 * type that does not count":
 *
 * > Every prescribed set of every type except `Warmup` counts — toward progression, adherence,
 * > volume, the completion ratio, records, and anything else that measures work.
 *
 * `Working`, `Top`, `Backoff`, `AMRAP` and `Dropset` are all real work the athlete performed and
 * the trainer prescribed. A back-off is not a lesser set; a drop set is not a footnote. Only a
 * warm-up is preparation rather than the thing being measured.
 *
 * **The discriminator is therefore `!== 'Warmup'`, and never an enumeration of the types that do
 * count.** A predicate written as `=== 'Working'`, or `is Working or Top`, is a bug waiting for the
 * next set type — and it has caused a real one every time it has been written. The practical
 * consequence worth re-checking whenever set types are touched: an athlete who performs a top set
 * and two back-offs performed **three** sets, and every count the trainer reads must say three.
 *
 * This module exists so there is exactly one function to grep for and exactly one place to change
 * if the rule ever moves. `muscleVolume` and `weekProjection` both call it; neither restates it.
 */

import type { SetDraft } from './planDraft.types';

/**
 * The one set type that does not count. Named rather than inlined so the comparison below reads as
 * the rule it is, and so a search for the string finds this file first.
 */
const NON_COUNTING_SET_TYPE = 'Warmup';

/**
 * Does this set count as work?
 *
 * `setType` is nullable on the wire, and a null COUNTS: a set the backend did not label is a set
 * the trainer prescribed, and the only claim this rule makes is about warm-ups specifically.
 * Compared case-insensitively because the type is a PascalCase enum name on the wire and nothing
 * downstream should break on a casing change that means nothing.
 */
export function countsAsWork(setType: string | null | undefined): boolean {
  return setType?.toLowerCase() !== NON_COUNTING_SET_TYPE.toLowerCase();
}

/** How many of these sets count as work. */
export function countingSetCount(sets: readonly SetDraft[]): number {
  return sets.reduce((total, set) => (countsAsWork(set.setType) ? total + 1 : total), 0);
}

/**
 * The exercise role that puts an exercise OUTSIDE the set counter altogether.
 *
 * This is a second, structural exclusion and it is not the same rule as the one above. The v3
 * canon renders a session as two named blocks — "Rozgrzewka" and "Praca główna" — and prints
 * "poza licznikiem serii" under the warm-up list: a warm-up-ROLE exercise contributes neither a
 * counted set nor a muscle point, whatever its individual sets are typed as.
 *
 * The two rules answer different questions and both come from the canon. The role decides which
 * BLOCK an exercise renders in and whether it is measured at all; the set type decides whether one
 * set inside a measured exercise counts.
 */
const NON_COUNTING_ROLE = 'Warmup';

/** Is this exercise part of the session's warm-up block rather than its main work? */
export function isWarmupRole(role: string | null | undefined): boolean {
  return role?.toLowerCase() === NON_COUNTING_ROLE.toLowerCase();
}
